import React, {useState, useMemo, useEffect, useRef} from 'react';
import {Container, Image, Panel, Row} from '@wonderlandengine/react-ui/components';
import {Align, Justify, PositionType, YogaNodeProps} from '@wonderlandengine/react-ui';
import {colorSwatch} from '../utils/colorSwatch.js';
import {parseColor} from '../utils/utils.js';
import {useRotBMaterialContext} from '../utils/rotb-material-context.js';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {Slider, SliderItem, SliderRef} from './SliderComponent.js';
import {UiTexturesManager} from './ui-textures-manager.js';
import {RotatableImage} from './rotatable-image.js';

const _mapSliderItems = (children: React.ReactNode): SliderItem[] => {
    return React.Children.map(children, (child) => {
        if (!child) {
            return null;
        }
        const item = (child as React.ReactElement).props as SliderItem;
        if (!item) {
            return null;
        }
        return item;
    }).filter((item): item is SliderItem => item !== null);
};

export const CarouselItem = (props: SliderItem) => {
    // doesn't render anything, just a placeholder for the carousel
    return <></>;
};

export const Carousel = (
    props: React.PropsWithChildren<YogaNodeProps> & {
        loop: boolean;
        onSelectionChanged?: (item: SliderItem) => void;
    }
) => {
    const rotbMC = useRotBMaterialContext();
    const ground = useMemo(() => {
        const m = rotbMC?.gradientMaterial.clone() as FlatOpaqueTextured;
        m.setColor(parseColor(colorSwatch.SliderGround));
        return m;
    }, []);

    const [sliderItems, setSliderItems] = useState(_mapSliderItems(props.children));
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [canScrollPrevious, setCanScrollPrevious] = useState(false);

    useEffect(() => {
        const newSliderItems = _mapSliderItems(props.children);
        setSliderItems(newSliderItems);
    }, [props.children]);

    // reference to call into the Slider component
    const sliderRef = useRef<SliderRef>(null);

    // To go to next item
    const handleNextClick = () => {
        sliderRef.current?.next();
    };

    // To go to previous item
    const handlePrevClick = () => {
        sliderRef.current?.previous();
    };
    // Update scroll status
    const updateScrollState = () => {
        if (sliderRef.current) {
            setCanScrollNext(sliderRef.current.canScrollNext);
            setCanScrollPrevious(sliderRef.current.canScrollPrevious);
        }
    };
    // Initialize and update scroll status when slider is ready
    useEffect(() => {
        if (sliderRef.current) {
            updateScrollState();
        }
    }, [sliderItems]);

    // Update scroll status when selection changes
    const handleSelectionChange = (i: number) => {
        if (props.onSelectionChanged) {
            props.onSelectionChanged(sliderItems[i]);
        }
        updateScrollState();
    };

    return (
        <Panel
            width={500}
            height={props.height ?? 200}
            rounding={0}
            backgroundColor={colorSwatch.SliderBackground}
        >
            <Panel
                material={ground}
                width="100%"
                top={((props.height as number) ?? 200) - 100}
                height={100}
                rounding={0}
                border={0}
            ></Panel>
            <Slider
                ref={sliderRef}
                top={-45}
                width={500}
                height={200}
                justifyContent={Justify.Center}
                z={0.01}
                items={sliderItems}
                onSelectionChange={handleSelectionChange}
            ></Slider>
            <Row
                position={PositionType.Absolute}
                width={480}
                height={props.height ?? 200}
                justifyContent={Justify.SpaceBetween}
                alignItems={Align.Center}
                alignSelf={Align.Center}
            >
                <Container width={25} height={25}>
                    {canScrollPrevious && (
                        <RotatableImage
                            rounding={0}
                            width={25}
                            height={25}
                            src={UiTexturesManager.instance.arrow}
                            onClick={handlePrevClick}
                            rotation={180}
                        />
                    )}
                </Container>

                <Container width={25} height={25}>
                    {canScrollNext && (
                        <Image
                            rounding={0}
                            width={25}
                            height={25}
                            src={UiTexturesManager.instance.arrow}
                            onClick={handleNextClick}
                        />
                    )}
                </Container>
            </Row>
        </Panel>
    );
};
